import Button from '@jetbrains/ring-ui/components/button/button'
import classNames from 'classnames'
import plur from 'plur'
import * as React from 'react'

import type {LocationProps} from '../../../../routes'
import {isQueueScreen} from '../../../../routes'
import {noop, noopAsync} from '../../../../utils/empty'
import {nth} from '../../../../utils/printCount'
import styles from '../PagerWarning/PagerWarning.css'

type DefaultProps = Partial<LocationProps> & {
  gotoLastPage?: () => unknown
  lookDeeper?: () => unknown
}
type Props = DefaultProps & {
  lookupLimit?: number
  lookupLimitWarning?: boolean | null | undefined
  total: number
  hasMore?: boolean | null | undefined
  currentPage: number
  noItemsOnPageWarning: boolean
  hasError?: boolean
  className?: string
}

export default class BuildPagerWarning extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    lookDeeper: noop,
    gotoLastPage: noop,
    navigate: noopAsync,
  }

  scope(): string {
    const {lookupLimit, lookupLimitWarning} = this.props
    return lookupLimitWarning === true && lookupLimit != null
      ? `${String(lookupLimit)} most recent ${plur('build', lookupLimit)}`
      : 'whole build history'
  }

  builds(): string {
    const {total, hasMore, lookupLimitWarning} = this.props
    return `${total > 0 ? total : 'No'}${
      total > 0 && hasMore === true && lookupLimitWarning !== true ? '+' : ''
    } ${plur('build', total)} found`
  }

  buildsInScope(): string {
    return `${this.builds()} in ${this.scope()}`
  }

  getGoToLastPageButton(): React.ReactNode {
    return (
      <Button className={styles.button} text onClick={this.props.gotoLastPage}>
        {'go to last loaded page'}
      </Button>
    )
  }

  getLookDeeperButton(): React.ReactNode {
    return (
      <Button className={styles.button} text onClick={this.props.lookDeeper}>
        {'look deeper in history'}
      </Button>
    )
  }

  getNoBuildOnPageWarning(): React.ReactNode {
    const {currentPage} = this.props
    return (
      <React.Fragment>
        {`${this.buildsInScope()} and there are no builds on requested ${nth(
          currentPage,
        )} page. You can`}
        {this.getGoToLastPageButton()}
      </React.Fragment>
    )
  }

  getBothLimitsWarning(): React.ReactNode {
    const {currentPage} = this.props
    return (
      <React.Fragment>
        {`${this.buildsInScope()}. No builds on requested ${nth(currentPage)} page. You can`}
        {this.getGoToLastPageButton()}
        {'or'}
        {this.getLookDeeperButton()}
      </React.Fragment>
    )
  }

  geLookupLimitWarning(): React.ReactNode {
    return (
      <React.Fragment>
        {`${this.buildsInScope()}. You can`}
        {this.getLookDeeperButton()}
      </React.Fragment>
    )
  }

  getErrorMessage(): React.ReactNode {
    return <span className={styles.error}>{'Error while loading builds'}</span>
  }

  getContent(): React.ReactNode {
    const {
      lookupLimitWarning,
      total,
      noItemsOnPageWarning,
      hasError,
      location = window.location,
    } = this.props
    const bothLimitsReached = noItemsOnPageWarning && lookupLimitWarning

    if (bothLimitsReached === true) {
      return this.getBothLimitsWarning()
    } else if (lookupLimitWarning === true) {
      return this.geLookupLimitWarning()
    } else if (noItemsOnPageWarning) {
      return this.getNoBuildOnPageWarning()
    } else if (total > 0) {
      return this.builds()
    } else if (hasError === true) {
      return this.getErrorMessage()
    } else if (isQueueScreen(location)) {
      return 'Build queue is empty'
    }

    return this.buildsInScope()
  }

  render(): React.ReactNode {
    return (
      <div data-test-pager-warning className={classNames(styles.warning, this.props.className)}>
        {this.getContent()}
      </div>
    )
  }
}
